import { Card } from "@/components/ui/card";
import { fmtRupiah } from "@/lib/format";
import { STAGE_IN_WORK } from "@/lib/constants";

const val = (x, empty = "Belum Diinput") => (x == null ? empty : fmtRupiah(x));

// Aggregates one row per project; units are counted from distinct vehicle_id (no double count).
export function computeTotals(projects, fin) {
  const t = { projects: projects.length, units: new Set(), masuk: new Set(), produksi: new Set(), finish: new Set(), keluar: new Set(), closed: 0,
    deal: 0, dealCount: 0, rap: 0, rapCount: 0, actual: 0, actualCount: 0, est: 0, estCount: 0, act: 0, actCount: 0 };
  projects.forEach((p) => {
    const u = p.vehicle_id || p.id;
    if (p.vehicle_id) t.units.add(p.vehicle_id);
    if (p.unit_in_at || STAGE_IN_WORK.includes(p.stage) || ["Ready", "QC", "Unit Out", "Closed"].includes(p.stage)) t.masuk.add(u);
    if (STAGE_IN_WORK.includes(p.stage)) t.produksi.add(u);
    if (["Ready", "QC"].includes(p.stage)) t.finish.add(u);
    if (["Unit Out", "Closed"].includes(p.stage)) t.keluar.add(u);
    if (p.stage === "Closed") t.closed += 1;
    const f = fin[p.id];
    if (!f) return;
    if (f.contract_total != null && f.deal_locked) { t.deal += f.contract_total; t.dealCount += 1; }
    if (f.rap_plan != null) { t.rap += f.rap_plan; t.rapCount += 1; }
    if (f.actual_total != null) { t.actual += f.actual_total; t.actualCount += 1; }
    if (f.est_margin != null) { t.est += f.est_margin; t.estCount += 1; }
    if (f.actual_margin != null) { t.act += f.actual_margin; t.actCount += 1; }
  });
  t.units = t.units.size;
  ["masuk", "produksi", "finish", "keluar"].forEach((k) => { t[k] = t[k].size; });
  return t;
}

const Stat = ({ label, value, testid, big }) => (
  <div className="rounded-lg border bg-white p-3">
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
    <div className={`font-extrabold text-navy ${big ? "text-xl" : "text-2xl"}`} data-testid={testid}>{value}</div>
  </div>
);

export const MasterSummary = ({ t, canDeal }) => (
  <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="pm-summary">
    <Stat label="Seluruh Proyek" value={t.projects} testid="pm-sum-projects" />
    <Stat label="Unit Masuk" value={t.masuk} testid="pm-sum-masuk" />
    <Stat label="Dalam Produksi" value={t.produksi} testid="pm-sum-produksi" />
    <Stat label="Finish (Ready/QC)" value={t.finish} testid="pm-sum-finish" />
    <Stat label="Unit Keluar" value={t.keluar} testid="pm-sum-keluar" />
    {canDeal && <Stat label="Total Harga Deal" value={fmtRupiah(t.deal)} testid="pm-sum-deal" big />}
  </div>
);

export const MasterTotals = ({ title, t, canDeal, profit, testid }) => (
  <Card className="p-4 border-primary/40" data-testid={testid}>
    <div className="text-xs font-bold text-primary uppercase mb-2">{title}</div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
      <div>TOTAL PROJECT: <b data-testid={`${testid}-projects`}>{t.projects}</b></div>
      <div>TOTAL UNIT: <b data-testid={`${testid}-units`}>{t.units}</b></div>
      <div>CLOSED: <b>{t.closed}</b></div>
      {canDeal && <div className="col-span-2 md:col-span-1">TOTAL HARGA DEAL: <b className="text-primary text-base" data-testid={`${testid}-deal`}>{fmtRupiah(t.deal)}</b> <span className="text-[10px] text-muted-foreground">({t.dealCount} deal terkunci)</span></div>}
      {profit && <>
        <div>TOTAL RAP: <b data-testid={`${testid}-rap`}>{t.rapCount ? fmtRupiah(t.rap) : "Belum Diinput"}</b></div>
        <div>TOTAL AKTUAL: <b data-testid={`${testid}-actual`}>{t.actualCount ? fmtRupiah(t.actual) : "Belum Diinput"}</b></div>
        <div>ESTIMASI MARGIN: <b data-testid={`${testid}-est`}>{t.estCount ? fmtRupiah(t.est) : "Belum Dapat Dihitung"}</b> <span className="text-[10px] text-muted-foreground">({t.estCount} proyek)</span></div>
        <div>MARGIN AKTUAL (Closed): <b data-testid={`${testid}-act`}>{t.actCount ? fmtRupiah(t.act) : "Belum Ada"}</b></div>
      </>}
    </div>
  </Card>
);

export const ProjectCard = ({ p, f, canDeal, profit, onClick }) => (
  <Card className="p-3 cursor-pointer hover:border-primary transition-colors" onClick={onClick} data-testid={`project-card-${p.project_id}`}>
    <div className="flex items-start justify-between gap-2">
      <span className="font-mono-k3 text-xs font-bold text-primary">{p.project_id}</span>
      <span data-testid={`project-stage-${p.project_id}`}>{p.stageBadge}</span>
    </div>
    <div className="font-bold text-navy text-lg mt-0.5">{p.plate}</div>
    <div className="text-sm font-medium">{p.customer_name}</div>
    <div className="text-xs text-muted-foreground">{p.work_type} · PJ: {p.penanggung_jawab_name || <span className="text-primary">Belum ada PJ</span>} · Target: {p.targetLabel}</div>
    {canDeal && (
      <div className="mt-2 grid grid-cols-2 gap-2 border-t pt-2">
        <div><div className="text-[10px] text-muted-foreground">HARGA DEAL/SPK</div>
          <div className={`font-extrabold ${f?.deal_locked ? "text-navy text-base" : "text-amber-700 text-sm"}`} data-testid={`project-deal-${p.project_id}`}>{f?.deal_locked ? fmtRupiah(f.contract_total) : "BELUM DEAL"}</div></div>
        {profit && <div><div className="text-[10px] text-muted-foreground">BIAYA AKTUAL</div>
          <div className="font-extrabold text-navy text-base" data-testid={`project-actual-${p.project_id}`}>{val(f?.actual_total)}</div></div>}
      </div>
    )}
  </Card>
);
